import { apiClient } from '@/lib/api-client';
import { secureFileService } from '@/services/secureFileService';

/**
 * File Service for handling file uploads and downloads
 * Uses the backend File API module
 */
export const FileService = {
  /**
   * Upload file to File API
   * @param {File} file - The file to upload
   * @param {string} type - Content type (scorm, pdf, video, epub)
   * @returns {Promise} Response with file information
   */
  uploadFile: async (file) => {
    try {
      const formData = new FormData();
      formData.append('files[]', file);
      
      const response = await apiClient.post('/file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('File upload error:', error);
      throw error;
    }
  },

  /**
   * Get download link for a file
   * @param {string} fileId - The file ID
   * @returns {Promise} Response with download link
   */
  getDownloadLink: async (fileId) => {
    try {
      const response = await apiClient.get(`/api/file/download-link/${fileId}`);
      return response.data;
    } catch (error) {
      console.error('Get download link error:', error);
      throw error;
    }
  },

  /**
   * Download file using token
   * @param {string} token - Download token
   * @returns {Promise} Response with file blob
   */
  downloadFile: async (token) => {
    try {
      const response = await apiClient.get(`/api/file/download/${token}`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('File download error:', error);
      throw error;
    }
  },

  /**
   * Upload file using public endpoint (no auth required)
   * @param {File} file - The file to upload
   * @param {string} type - Content type
   * @returns {Promise} Response with file information
   */
  uploadFilePublic: async (file, type = 'scorm') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      
      const response = await apiClient.post('/api/file/file-public', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('Public file upload error:', error);
      throw error;
    }
  },

  // ===== SECURE FILE METHODS =====
  
  /**
   * Upload file to secure location with authorization token protection
   * @param {File} file - The file to upload
   * @returns {Promise} Response with secure file information
   */
  uploadFileSecure: async (file, contentType) => {
    try {
      // Validate file before upload
      const validation = secureFileService.validateFile(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      const response = await secureFileService.uploadFile(file, contentType);
      return response;
    } catch (error) {
      console.error('Secure file upload error:', error);
      throw error;
    }
  },

  /**
   * Upload multiple files to secure location
   * @param {File[]} files - Array of files to upload
   * @returns {Promise} Response with secure file information
   */
  uploadFilesSecure: async (files) => {
    try {
      // Validate all files before upload
      for (const file of files) {
        const validation = secureFileService.validateFile(file);
        if (!validation.valid) {
          throw new Error(`File ${file.name}: ${validation.error}`);
        }
      }

      const response = await secureFileService.uploadFiles(files);
      return response;
    } catch (error) {
      console.error('Secure files upload error:', error);
      throw error;
    }
  },

  /**
   * Get secure file information
   * @param {string} token - Secure file token
   * @returns {Promise} Response with file information
   */
  getSecureFileInfo: async (token) => {
    try {
      const response = await secureFileService.getFileInfo(token);
      return response;
    } catch (error) {
      console.error('Get secure file info error:', error);
      throw error;
    }
  },

  /**
   * Download secure file using secure URL (handles authentication automatically)
   * @param {string} secureUrl - Secure file URL
   * @param {string} filename - Optional filename for download
   */
  downloadSecureFile: async (secureUrl, filename) => {
    try {
      await secureFileService.downloadFile(secureUrl, filename);
    } catch (error) {
      console.error('Secure file download error:', error);
      throw error;
    }
  },

  /**
   * Download secure file using file ID (generates token on-demand)
   * @param {string} fileId - File ID
   * @param {string} filename - Optional filename for download
   */
  downloadSecureFileById: async (fileId, filename) => {
    try {
      await secureFileService.downloadFileById(fileId, filename);
    } catch (error) {
      console.error('Secure file download error:', error);
      throw error;
    }
  },

  /**
   * Open secure file in new tab using secure URL (handles authentication automatically)
   * @param {string} secureUrl - Secure file URL
   */
  openSecureFile: (secureUrl) => {
    try {
      secureFileService.openFileInNewTab(secureUrl);
    } catch (error) {
      console.error('Open secure file error:', error);
      throw error;
    }
  },

  /**
   * Open secure file in new tab using file ID (generates token on-demand)
   * @param {string} fileId - File ID
   */
  openSecureFileById: async (fileId) => {
    try {
      await secureFileService.openFileInNewTabById(fileId);
    } catch (error) {
      console.error('Open secure file error:', error);
      throw error;
    }
  },

  /**
   * Get secure file preview URL using file ID (generates token on-demand)
   * @param {string} fileId - File ID
   * @returns {Promise<string>} Preview URL
   */
  getSecureFilePreviewUrl: async (fileId) => {
    try {
      return await secureFileService.getPreviewUrl(fileId);
    } catch (error) {
      console.error('Get secure file preview URL error:', error);
      throw error;
    }
  },

  /**
   * Generate download token for a file
   * @param {string} fileId - File ID
   * @returns {Promise} Token response
   */
  generateSecureToken: async (fileId) => {
    try {
      const response = await secureFileService.generateDownloadToken(fileId);
      return response;
    } catch (error) {
      console.error('Generate secure token error:', error);
      throw error;
    }
  },

  /**
   * Check if file type supports preview
   * @param {string} extension - File extension
   * @returns {boolean} Whether file supports preview
   */
  supportsPreview: (extension) => {
    return secureFileService.supportsPreview(extension);
  },

  /**
   * Get file type icon
   * @param {string} extension - File extension
   * @returns {string} Icon emoji
   */
  getFileTypeIcon: (extension) => {
    return secureFileService.getFileTypeIcon(extension);
  },

  /**
   * Format file size
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  formatFileSize: (bytes) => {
    return secureFileService.formatFileSize(bytes);
  },
};

export default FileService;
